import React, { useState, useEffect, useCallback } from 'react';

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

const ServicesSettings = ({ settings, onInputChange }) => {
  // Local state for immediate UI updates
  const [localServices, setLocalServices] = useState(settings.services.join("\n"));
  const [localKeywords, setLocalKeywords] = useState(settings.emergency_keywords.join("\n"));

  // Update local state when settings change from outside
  useEffect(() => {
    setLocalServices(settings.services.join("\n"));
  }, [settings.services]);

  useEffect(() => {
    setLocalKeywords(settings.emergency_keywords.join("\n"));
  }, [settings.emergency_keywords]);

  // Debounced update functions
  const updateServices = useCallback(
    debounce((value) => {
      const serviceArray = value
        .split("\n")
        .filter((s) => s.trim())
        .map((s) => s.trim());
      onInputChange("services", serviceArray);
    }, 500), // 500ms delay
    [onInputChange]
  );

  const updateKeywords = useCallback(
    debounce((value) => {
      const keywordArray = value
        .split("\n")
        .filter((k) => k.trim())
        .map((k) => k.trim());
      onInputChange("emergency_keywords", keywordArray);
    }, 500), // 500ms delay
    [onInputChange]
  );

  // Handle local state changes
  const handleServicesChange = useCallback((e) => {
    const value = e.target.value;
    setLocalServices(value);
    updateServices(value);
  }, [updateServices]);

  const handleKeywordsChange = useCallback((e) => {
    const value = e.target.value;
    setLocalKeywords(value);
    updateKeywords(value);
  }, [updateKeywords]);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          🔧 Services & Emergency Settings
        </h3>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Services You Offer
            </label>
            <p className="text-sm text-gray-500 mb-3">
              List the services your business provides (one per line). Your AI
              assistant will know about these services.
            </p>
            <textarea
              value={localServices}
              onChange={handleServicesChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={6}
              placeholder="HVAC Repair&#10;Air Conditioning Installation&#10;Heating System Maintenance&#10;Emergency Service&#10;Duct Cleaning"
            />
            <p className="text-xs text-gray-500 mt-2">
              Type each service on a separate line. Changes are saved automatically after you stop typing.
            </p>
          </div>

          {/* ✅ NEW: Emergency Service Toggle */}
          <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="h-10 w-10 bg-red-100 rounded-lg flex items-center justify-center">
                <span className="text-xl">🚨</span>
              </div>
              <div>
                <h4 className="font-medium text-red-900">
                  🚨 Emergency Service Availability
                </h4>
                <p className="text-sm text-red-700">
                  Configure 24/7 emergency service options for urgent customer needs
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Emergency Service Toggle */}
              <div className="flex items-center justify-between p-4 bg-white border border-red-200 rounded-lg">
                <div>
                  <h5 className="text-sm font-medium text-red-900">
                    24/7 Emergency Service Available
                  </h5>
                  <p className="text-sm text-red-600">
                    Allow emergency appointments outside normal business hours
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.emergency_service || false}
                    onChange={(e) =>
                      onInputChange("emergency_service", e.target.checked)
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                </label>
              </div>

              {/* Emergency Service Explanation */}
              <div className="bg-white border border-red-200 rounded-lg p-4">
                <h5 className="font-medium text-red-900 mb-2">
                  {settings.emergency_service ? "✅ Emergency Service Enabled" : "⚠️ Emergency Service Disabled"}
                </h5>
                {settings.emergency_service ? (
                  <div className="text-sm text-red-800 space-y-2">
                    <p>• <strong>Emergency calls are escalated immediately</strong> following your escalation rules</p>
                    <p>• Customers using emergency keywords trigger immediate escalation</p>
                    <p>• <strong>Configure escalation settings</strong> in the Escalation tab to control how you're notified</p>
                  </div>
                ) : (
                  <div className="text-sm text-gray-600 space-y-2">
                    <p>• Emergency appointments are restricted to business hours only</p>
                    <p>• AI will direct emergency callers to call emergency services if life-threatening</p>
                    <p>• All appointments must be within your normal operating hours</p>
                    <p>• No automatic escalation for emergency keywords</p>
                  </div>
                )}
              </div>

              {/* Escalation Integration Notice */}
              {settings.emergency_service && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div className="h-5 w-5 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                      <span className="text-xs">ℹ️</span>
                    </div>
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">Emergency Escalation Integration</p>
                      <p>Emergency calls automatically follow your escalation settings. Visit the <strong>Escalation tab</strong> to configure:</p>
                      <ul className="mt-2 space-y-1 ml-4 list-disc">
                        <li>Live transfer to your phone vs. callback notifications</li>
                        <li>SMS and email notification preferences</li>
                        <li>Which phone number to escalate to</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Emergency Keywords Section */}
          <div className={`transition-all ${settings.emergency_service ? 'opacity-100' : 'opacity-60'}`}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Emergency Keywords
              {!settings.emergency_service && (
                <span className="text-xs text-gray-500 ml-2">(Enable emergency service above to activate)</span>
              )}
            </label>
            <p className="text-sm text-gray-500 mb-3">
              Words that should trigger urgent response (one per line). When
              customers use these words, you'll be notified immediately.
            </p>
            <textarea
              value={localKeywords}
              onChange={handleKeywordsChange}
              disabled={!settings.emergency_service}
              className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !settings.emergency_service ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              rows={4}
              placeholder="emergency&#10;urgent&#10;flooding&#10;no heat&#10;no power&#10;burst pipe"
            />
            <p className="text-xs text-gray-500 mt-2">
              Type each keyword on a separate line. Changes are saved automatically after you stop typing.
            </p>
          </div>

          <div className="space-y-4 pt-4 border-t border-gray-200">
            <h4 className="font-medium text-gray-900">📅 Booking Settings</h4>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h5 className="text-sm font-medium text-gray-900">
                  Automatic Booking
                </h5>
                <p className="text-sm text-gray-500">
                  Allow your AI assistant to automatically book appointments
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.auto_booking_enabled}
                  onChange={(e) =>
                    onInputChange("auto_booking_enabled", e.target.checked)
                  }
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          {/* Preview sections to show what's been saved */}
          {settings.services.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h5 className="font-medium text-green-900 mb-2">
                ✅ Saved Services ({settings.services.length})
              </h5>
              <div className="text-sm text-green-800">
                {settings.services.map((service, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <span className="text-green-600">•</span>
                    <span>{service}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {settings.emergency_keywords.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h5 className="font-medium text-red-900 mb-2">
                🚨 Emergency Keywords ({settings.emergency_keywords.length})
                {!settings.emergency_service && (
                  <span className="text-xs text-red-600 ml-2">(Emergency service disabled)</span>
                )}
              </h5>
              <div className="text-sm text-red-800">
                {settings.emergency_keywords.map((keyword, index) => (
                  <span
                    key={index}
                    className={`inline-block px-2 py-1 rounded text-xs mr-2 mb-1 ${
                      settings.emergency_service 
                        ? 'bg-red-200 text-red-800' 
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(ServicesSettings);